import React, { memo, useEffect, useMemo, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { Form } from 'react-final-form';
import classNames from 'classnames';
import { CircularProgress, Paper, makeStyles, Grid } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import { useSelector } from 'react-redux';

import ZipInput from '../../../calculator/components/ZipInput';
import useCustomNotify from 'hooks/useCustomNotify';
import { FromLocation } from 'icons';
import SubmitButton from 'components/buttons/SubmitButton';
import { FormErrorLabel } from 'components/form';
import { useCreatePreset } from 'pages/warehousePresets/utils';
import { get } from 'lodash';
import FormLeftColumn from './FormLeftColumn';
import FormRightColumn from './FormRightColumn';
import { getDefaultShipmentDate } from '../../../../services/helpers/defaultShipmentDateHelpers';
import { weightOzFormValidator, weightGmFormValidator } from 'components/form/validationRules';
import Dialog from 'components/common/Dialog';
import RolloDeliveryPromo from 'components/rolloDelivery/RolloDeliveryPromo';
import moment from 'moment';
import { isImperialUnitMeasurement } from 'services/helpers/unitsMeasurement';
import {
  convertOzToLbOz,
  convertGmToKgGm,
  getWeightOzValue,
  getWeightGmValue,
} from 'services/helpers/convertWeight';

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
  defaultHeader: {
    borderTopLeftRadius: theme.spacing(5.5),
    borderTopRightRadius: theme.spacing(5.5),
    marginTop: 0,
  },
  header: {
    background: theme.palette.secondary.light,
    borderRadius: theme.spacing(5.5),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  content: {
    padding: theme.spacing(1, 4, 4),
  },
  error: {
    padding: theme.spacing(0, 4),
  },
  footer: {
    padding: theme.spacing(3, 4, 4),
  },
}));

const requiredZip = (address) => {
  return address?.postalCode ? undefined : 'Required';
};

const RatesProgress = () => {
  const [text, setText] = useState('Requesting Rates');

  useEffect(() => {
    const intervalId = setInterval(() => setText('Calculating Optimal Rate'), 2000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <>
      <CircularProgress size={24} />
      &nbsp;{text}
    </>
  );
};

const GroupedForm = (props) => {
  const isImperial = isImperialUnitMeasurement();

  const normalizeCarriers = (values) => (Array.isArray(values) ? [...values].sort() : []);

  const normalizeOptions = (options) => ({
    insurance: Boolean(options?.insurance),
    signature: Boolean(options?.signature),
  });

  const normalizeService = (service) => (service === '' || service === undefined ? null : service);

  const normalizeNullableValue = (value) => (value === '' || value === undefined ? null : value);

  const normalizePackageInfo = (info, weightValue) => {
    const packageInfo = info ?? {};
    const size = packageInfo.size ?? {};

    const normalized: Record<string, any> = {
      additionalHandling: packageInfo.additionalHandling ?? null,
      cost: null,
      id: packageInfo.id ?? null,
      insuredValue: normalizeNullableValue(packageInfo.insuredValue),
      name: packageInfo.name ?? 'Custom',
      provider: packageInfo.provider ?? null,
      size: {
        width: normalizeNullableValue(size.width),
        height: normalizeNullableValue(size.height),
        length: normalizeNullableValue(size.length),
      },
      type: packageInfo.type ?? 'PACKAGE',
    };

    if (isImperial) {
      normalized.weightOz = weightValue ?? 0;
    } else {
      normalized.weightGm = weightValue ?? 0;
    }

    return normalized;
  };

  const getComparableCarriers = (selected, request) => {
    if (Array.isArray(request) && request.length) {
      return normalizeCarriers(request);
    }

    return normalizeCarriers(selected);
  };

  const dataProvider = useDataProvider();
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [prevFromId, setPrevFromId] = useState<string>('');
  const [showDeliveryPromo, setShowDeliveryPromo] = useState<boolean>(false);
  const [defaultWarehouse, setDefaultWarehouse] = useState<any>(null);
  const [latestWarehouses, setLatestWarehouses] = useState<any>(null);
  const [currentOrders, setCurrentOrders] = useState<any[]>([]);
  const classes = useStyles();
  const notify = useCustomNotify();
  const { id } = useParams();
  const createPreset = useCreatePreset();

  const rolloAccountId = useSelector((store) => store.rolloDelivery.rolloAccountId);

  const {
    selectedIds,
    onSubmit,
    warehousePresets,
    carriers,
    packageTypes,
    updatedGroupRule,
    setUpdatedGroupRule,
    groupSettings,
    showGroupSwitch,
    services,
    deliveryTimes,
    setGroupSettings,
  } = props;

  // Default warehouse can be part of other lists; dedupe by id to avoid duplicate options.
  const dedupeById = (list: any[]) => {
    const seen = new Set();
    return (list ?? []).filter((item) => {
      const itemId = item?.id;
      if (!itemId) return true;
      if (seen.has(itemId)) return false;
      seen.add(itemId);
      return true;
    });
  };

  useEffect(() => {
    dataProvider
      .getList('recentFromAddresses', { limit: 1 })
      .then(({ data }) => {
        setLatestWarehouses(data);
      })
      .catch((error: any) => {
        const errorMessage = error?.message || 'Error getting recent from addresses';
        notify(errorMessage, 'error');
      });
  }, [dataProvider, notify]);

  useEffect(() => {
    dataProvider
      .get('warehouseDefault')
      .then(({ data }) => {
        // Assumes warehouseDefault matches the warehouse preset shape used by ZipAutocomplete.
        setDefaultWarehouse(data);
      })
      .catch((error: any) => {
        const errorMessage = error?.message || 'Error getting default warehouse';
        notify(errorMessage, 'error');
      });
  }, [dataProvider, notify]);

  useEffect(() => {
    const orders = JSON.parse(sessionStorage.getItem('selectedOrders') ?? '[]');
    setCurrentOrders(orders);
  }, []);

  const warehouseOptions = useMemo(
    () =>
      dedupeById([...(defaultWarehouse ? [defaultWarehouse] : []), ...(warehousePresets ?? [])]),
    [defaultWarehouse, warehousePresets]
  );

  const createGroupSettings = (data) => {
    const {
      selectedCarriers,
      selectedService,
      requestCarrier,
      from,
      options,
      shipmentDate,
      packageInfo,
    } = data;

    const requestData = {
      carriers: deliveryTimes.some((time) => time.code === selectedService)
        ? selectedCarriers
        : requestCarrier,
      companyLocationPresetId: from.id,
      deliveryServiceCode: selectedService,
      insurance: options.insurance,
      signature: options.signature,
      shipmentDate: shipmentDate,
      packageInfo: {
        ...packageInfo[0],
      },
      orders: selectedIds,
    };

    dataProvider
      .create('groupSettings', {
        id,
        data: requestData,
      })
      .then(() => setGroupSettings(requestData))
      .catch((error: any) => {
        const errorMessage = error?.message || 'Error creating group settings';
        notify(errorMessage, 'error');
      });
  };

  const getCarriers = (savedCarriers) => {
    if (!savedCarriers) return undefined;

    const filteredCarriers = savedCarriers?.length
      ? carriers.filter((carrier) => savedCarriers.includes(carrier.provider.code))
      : carriers;

    return filteredCarriers?.map((carrier) => carrier.provider.code);
  };

  const initData = useMemo(
    () => {
      const currentPackageInfo = updatedGroupRule?.packageInfo[0] ?? groupSettings?.packageInfo;

      const { weightLb, weightOz } = convertOzToLbOz(currentPackageInfo?.weightOz);
      const { weightKg, weightGm } = convertGmToKgGm(currentPackageInfo?.weightGm);

      if (currentPackageInfo) {
        isImperial ? delete currentPackageInfo.weightGm : delete currentPackageInfo.weightOz;
      }

      return {
        from: warehousePresets
          ? updatedGroupRule?.from ??
            warehouseOptions?.find(
              (preset) => preset.id === groupSettings?.companyLocationPresetId
            ) ??
            defaultWarehouse ??
            // Risk: recent-from fallback may not exist in preset options, so the label can be blank.
            (latestWarehouses?.length ? latestWarehouses[0] : warehouseOptions[0])
          : null,
        packageInfo: [
          {
            ...currentPackageInfo,
            additionalHandling: currentPackageInfo?.additionalHandling ?? null,
            id: currentPackageInfo?.id ?? null,
            name: currentPackageInfo?.name ?? 'Custom',
            type: currentPackageInfo?.type ?? 'PACKAGE',
            provider: currentPackageInfo?.provider ?? null,
            size: {
              width: currentPackageInfo?.size?.width ?? null,
              height: currentPackageInfo?.size?.height ?? null,
              length: currentPackageInfo?.size?.length ?? null,
            },
            weight: isImperial
              ? {
                  lb: weightLb,
                  oz: weightOz,
                }
              : {
                  kg: weightKg,
                  gm: weightGm,
                },
            insuredValue: currentPackageInfo?.insuredValue ?? null,
            cost: currentPackageInfo?.cost ?? null,
          },
        ],
        options: {
          signature: updatedGroupRule?.options?.signature ?? groupSettings?.signature ?? false,
          insurance: updatedGroupRule?.options?.insurance ?? groupSettings?.insurance ?? false,
        },
        selectedCarriers:
          getCarriers(updatedGroupRule?.selectedCarriers) ??
          getCarriers(groupSettings?.carriers) ??
          getCarriers([]),
        selectedService:
          updatedGroupRule?.selectedService ??
          groupSettings?.deliveryServiceCode ??
          '__FILTER_UPTO_3_DAYS',
        requestCarrier: updatedGroupRule?.requestCarrier ?? groupSettings?.carriers ?? [],
        packageName: '',
        shipmentDate:
          updatedGroupRule?.shipmentDate ?? getDefaultShipmentDate(moment().format('YYYY-MM-DD')),
        isGroupSave: true,
        availableServices: [...deliveryTimes, ...services],
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      groupSettings,
      deliveryTimes,
      services,
      warehousePresets,
      warehouseOptions,
      defaultWarehouse,
      updatedGroupRule,
      latestWarehouses,
    ]
  );

  useEffect(() => {
    if (
      !currentOrders?.some((order) => order.shippingAddress.city.toLowerCase() !== 'las vegas') &&
      initData?.from?.city?.toLowerCase() === 'las vegas' &&
      prevFromId !== initData?.from?.id &&
      rolloAccountId &&
      currentOrders.length > 0
    ) {
      const timeoutId = setTimeout(() => setShowDeliveryPromo(true), 200);
      return () => clearTimeout(timeoutId);
    }
  }, [currentOrders, initData?.from?.city, initData?.from?.id, prevFromId, rolloAccountId]);

  const initRatesCalculating = (data) => {
    if (data.isGroupSave) {
      createGroupSettings(data);
    }

    if (data.isPackageSave) {
      const packageType = packageTypes.find((item) => item.code === data.packageInfo[0].type);

      return createPreset({
        packageName: data.packageName,
        packageInfo: data.packageInfo[0],
        packageType,
      })
        .then(() => {
          notify('Preset was saved', 'success');
          onSubmit(data, id);
        })
        .catch((error: unknown) => {
          const errorMessage =
            error instanceof Error ? error.message : 'Error saving shipping preset';
          notify(errorMessage, 'warning');
        });
    }

    onSubmit(data, id);
  };

  const handleSubmit = (data) => {
    const weightOz = getWeightOzValue(data.packageInfo[0].weight.lb, data.packageInfo[0].weight.oz);
    const weightGm = getWeightGmValue(data.packageInfo[0].weight.kg, data.packageInfo[0].weight.gm);

    const savedPackageInfo = Array.isArray(groupSettings?.packageInfo)
      ? groupSettings?.packageInfo?.[0]
      : groupSettings?.packageInfo;

    const getSavedWeightValue = () => {
      if (!savedPackageInfo) {
        return null;
      }

      if (isImperial) {
        if (savedPackageInfo.weightOz !== undefined && savedPackageInfo.weightOz !== null) {
          return savedPackageInfo.weightOz;
        }

        const hasImperialParts =
          savedPackageInfo?.weight?.lb != null || savedPackageInfo?.weight?.oz != null;

        if (hasImperialParts) {
          return getWeightOzValue(
            savedPackageInfo?.weight?.lb ?? 0,
            savedPackageInfo?.weight?.oz ?? 0
          );
        }

        return null;
      }

      if (savedPackageInfo?.weightGm !== undefined && savedPackageInfo?.weightGm !== null) {
        return savedPackageInfo.weightGm;
      }

      const hasMetricParts =
        savedPackageInfo?.weight?.kg != null || savedPackageInfo?.weight?.gm != null;

      if (hasMetricParts) {
        return getWeightGmValue(
          savedPackageInfo?.weight?.kg ?? 0,
          savedPackageInfo?.weight?.gm ?? 0
        );
      }

      return null;
    };

    const savedWeightValue = getSavedWeightValue();

    const comparableSavedData = {
      fromId: groupSettings?.companyLocationPresetId ?? null,
      packageInfo: [normalizePackageInfo(savedPackageInfo, savedWeightValue)],
      options: normalizeOptions({
        insurance: groupSettings?.insurance,
        signature: groupSettings?.signature,
      }),
      carriers: normalizeCarriers(groupSettings?.carriers),
      selectedService: normalizeService(groupSettings?.deliveryServiceCode),
      packageName: '',
      shipmentDate: null,
      isGroupSave: true,
      availableServices: null,
    };

    const comparableCarriers = getComparableCarriers(data.selectedCarriers, data.requestCarrier);

    const comparableFormData = {
      fromId: data.from?.id ?? null,
      packageInfo: [normalizePackageInfo(data.packageInfo[0], isImperial ? weightOz : weightGm)],
      options: normalizeOptions(data.options),
      carriers: comparableCarriers,
      selectedService: normalizeService(data.selectedService),
      packageName: '',
      shipmentDate: null,
      isGroupSave: true,
      availableServices: null,
    };

    const newData = {
      ...data,
      packageInfo: [
        {
          ...data.packageInfo[0],
          ...(isImperial ? { weightOz } : { weightGm }),
        },
      ],
    };

    if (!isEqual(comparableSavedData, comparableFormData) && !showGroupSwitch && data.isGroupSave) {
      setUpdatedGroupRule(newData);
      setShowConfirm(true);
      return;
    }

    setUpdatedGroupRule(newData);
    initRatesCalculating(newData);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    initRatesCalculating({ ...updatedGroupRule, isGroupSave: false });
  };

  const handleConfirm = () => {
    setShowConfirm(false);
    initRatesCalculating(updatedGroupRule);
  };

  const handleClose = () => {
    setShowConfirm(false);
  };

  if (!warehousePresets) {
    return <CircularProgress />;
  }

  return (
    <>
      <Dialog
        open={showConfirm}
        confirmText="Yes"
        cancelText="No"
        title="Shipment Rule Update"
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        onClose={handleClose}>
        Would you like to save your current selection as the new shipment rule?
      </Dialog>

      <Form
        onSubmit={handleSubmit}
        initialValues={initData}
        validate={
          isImperial
            ? weightOzFormValidator(
                packageTypes,
                'packageInfo[0].type',
                'packageInfo[0].weight.lb',
                'packageInfo[0].weight.oz'
              )
            : weightGmFormValidator(
                packageTypes,
                'packageInfo[0].type',
                'packageInfo[0].weight.kg',
                'packageInfo[0].weight.gm'
              )
        }
        render={({ handleSubmit, submitting, form, values, ...renderProps }) => {
          const packageType = packageTypes?.find(
            (i) => i.code === get(values, 'packageInfo[0].type')
          );

          const onTooltipClick = (e) => {
            e.preventDefault();
          };

          if (prevFromId !== values.from?.id) {
            setPrevFromId(values.from?.id);
          }

          const handleClosePromo = () => {
            setShowDeliveryPromo(false);
          };

          const handleConfirmPromo = () => {
            setShowDeliveryPromo(false);
            const rolloRatesRequestData = {
              ...values,
              requestCarrier: ['ROLLODELIVERY'],
              selectedService: 'GROUND_DELIVERY',
            };
            initRatesCalculating(rolloRatesRequestData);
          };

          return (
            <>
              <Paper className={classNames(classes.root, classes.defaultHeader)}>
                <form onSubmit={handleSubmit}>
                  <Paper className={classes.header}>
                    <ZipInput
                      name="from"
                      label="From ZIP Code"
                      icon={<FromLocation style={{ color: '#24C746' }} />}
                      warehousePresets={warehouseOptions}
                      validate={requiredZip}
                      redirectAfterSubmit={`orders/groups/${id}/calculate`}
                    />
                  </Paper>
                  <Grid container className={classes.content}>
                    <FormLeftColumn
                      carriers={carriers}
                      form={form}
                      values={values}
                      packageType={packageType}
                      isAdjustForm={false}
                      onTooltipClick={onTooltipClick}
                      showGroupSwitch={showGroupSwitch}
                      groupSettings={groupSettings}
                      updatedGroupRule={updatedGroupRule}
                      deliveryTimes={deliveryTimes}
                      groupId={id}
                    />
                    <FormRightColumn form={form} values={values} isAdjustForm={false} />
                  </Grid>
                  <FormErrorLabel className={classes.error} {...renderProps} />
                  <Grid container className={classes.footer} justify="center">
                    <SubmitButton
                      label="Calculate Shipping Rates"
                      size="large"
                      color="primary"
                      fullWidth={false}
                      submitting={submitting}
                      disabled={warehousePresets?.length === 0}
                      progress={<RatesProgress />}
                    />
                  </Grid>
                </form>
              </Paper>
              <RolloDeliveryPromo
                open={showDeliveryPromo}
                onCancel={handleClosePromo}
                onConfirm={handleConfirmPromo}
              />
            </>
          );
        }}
      />
    </>
  );
};

export default memo(GroupedForm);
